/**
 * Remplace les shortcodes OpenMoji dans le HTML par des images SVG (CDN).
 * Syntaxe :
 *   [openmoji{name[mot-clé]}] — mot-clé = annotation ou tag OpenMoji (anglais), ex. grinning face, snake
 *   [openmoji:1F600] — code hex Unicode (comme dans OpenMoji), ex. 1F41D pour abeille
 * Données : https://github.com/hfg-gmuend/openmoji — licence CC BY-SA 4.0
 */
(function () {
  'use strict';

  var VERSION = '15.0.0';
  var DATA_URL = 'https://cdn.jsdelivr.net/gh/hfg-gmuend/openmoji@' + VERSION + '/data/openmoji.json';
  var SVG_BASE = 'https://cdn.jsdelivr.net/gh/hfg-gmuend/openmoji@' + VERSION + '/color/svg/';
  var CACHE_KEY = 'euble_openmoji_hexmap_' + VERSION;

  var hexMap = null;
  var loadPromise = null;

  var RE = /\[\s*openmoji\s*\{\s*name\s*\[\s*([^\]]+?)\s*\]\s*\}\s*\]|\[\s*openmoji\s*:\s*([0-9A-Fa-f][0-9A-Fa-f-]*)\s*\]/gi;

  var SKIP_PARENT = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'NOSCRIPT']);

  /** Alias FR / abréviations (le reste vient du JSON OpenMoji, annotations en anglais). */
  var KEYWORD_ALIASES = {
    return: '1F519',
    retour: '1F519',
    back: '1F519',
    'back arrow': '1F519'
  };

  function normalizeHex(s) {
    return String(s || '')
      .trim()
      .toUpperCase()
      .replace(/^U\+/i, '');
  }

  function addKeys(map, hex, str) {
    if (!str) return;
    var parts = String(str)
      .split(',')
      .map(function (p) {
        return p.trim().toLowerCase().replace(/\s+/g, ' ');
      })
      .filter(Boolean);
    for (var i = 0; i < parts.length; i++) {
      var k = parts[i];
      if (k && map[k] === undefined) map[k] = hex;
    }
  }

  function buildHexMap(data) {
    var map = Object.create(null);
    for (var i = 0; i < data.length; i++) {
      var row = data[i];
      var hex = String(row.hexcode || '').toUpperCase();
      if (!hex) continue;
      addKeys(map, hex, row.annotation);
      addKeys(map, hex, row.tags);
      addKeys(map, hex, row.openmoji_tags);
    }
    return map;
  }

  function loadHexMap(cb) {
    if (hexMap) {
      cb(hexMap);
      return;
    }
    if (loadPromise) {
      loadPromise.then(function () {
        cb(hexMap);
      });
      return;
    }

    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        hexMap = JSON.parse(raw);
        cb(hexMap);
        return;
      }
    } catch (e) {
      /* ignore */
    }

    loadPromise = fetch(DATA_URL)
      .then(function (r) {
        if (!r.ok) throw new Error('OpenMoji JSON ' + r.status);
        return r.json();
      })
      .then(function (data) {
        hexMap = buildHexMap(data);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(hexMap));
        } catch (e) {
          /* quota */
        }
        loadPromise = null;
        cb(hexMap);
      })
      .catch(function (err) {
        loadPromise = null;
        console.warn('[OpenMoji]', err);
        hexMap = Object.create(null);
        cb(hexMap);
      });
  }

  function lookupKeyword(map, keyword) {
    var k = String(keyword || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    if (!k) return null;
    if (KEYWORD_ALIASES[k] !== undefined) return KEYWORD_ALIASES[k];
    if (map[k] !== undefined) return map[k];
    return null;
  }

  function makeInline(hex, label) {
    var span = document.createElement('span');
    span.className = 'openmoji-inline';
    span.setAttribute('role', 'img');
    if (!hex) {
      span.setAttribute('aria-label', 'Emoji introuvable : ' + label);
      span.classList.add('openmoji-inline--missing');
      span.textContent = '\u2753';
      return span;
    }
    var img = document.createElement('img');
    img.src = SVG_BASE + hex + '.svg';
    img.alt = '';
    img.setAttribute('draggable', 'false');
    img.loading = 'lazy';
    img.decoding = 'async';
    span.setAttribute('aria-label', label || 'Emoji');
    span.appendChild(img);
    return span;
  }

  function replaceInTextNode(textNode, map) {
    var text = textNode.nodeValue;
    if (!text || text.indexOf('openmoji') === -1) return;

    RE.lastIndex = 0;
    if (!RE.test(text)) return;
    RE.lastIndex = 0;

    var frag = document.createDocumentFragment();
    var last = 0;
    var m;
    while ((m = RE.exec(text)) !== null) {
      if (m.index > last) {
        frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      }
      var nameKey = m[1] != null ? m[1] : '';
      var hexDirect = m[2] != null ? m[2] : '';
      var hex = hexDirect ? normalizeHex(hexDirect) : lookupKeyword(map, nameKey);
      var label = hexDirect ? 'U+' + hex : nameKey.trim();
      frag.appendChild(makeInline(hex, label));
      last = m.index + m[0].length;
    }
    if (last < text.length) {
      frag.appendChild(document.createTextNode(text.slice(last)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
  }

  function parseRoot(root, map) {
    if (!root) return;
    var w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var batch = [];
    var n;
    while ((n = w.nextNode())) {
      if (!n.nodeValue || n.nodeValue.indexOf('openmoji') === -1) continue;
      var p = n.parentNode;
      if (!p || SKIP_PARENT.has(p.nodeName)) continue;
      if (p.closest && p.closest('[data-openmoji-skip]')) continue;
      if (p.closest && p.closest('.openmoji-inline')) continue;
      batch.push(n);
    }
    for (var i = batch.length - 1; i >= 0; i--) {
      replaceInTextNode(batch[i], map);
    }
  }

  function run(root) {
    var el = root || document.body;
    if (!el || el.textContent.indexOf('openmoji') === -1) return;
    loadHexMap(function (map) {
      parseRoot(el, map);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    run(document.body);
  });

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target && ev.target.textContent && ev.target.textContent.indexOf('openmoji') !== -1) {
      run(ev.target);
    }
  });

  document.addEventListener('euble:openmoji-parse', function (ev) {
    run((ev.detail && ev.detail.root) || document.body);
  });

  window.EubleOpenMoji = { parse: run, loadHexMap: loadHexMap };
})();
